import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import { Badge, Button, Card, EmptyState, Skeleton } from '@/ui/primitives'
import { Camp } from '@/ui/form'
import { Grup } from './comune'

type Obs = {
  id: number; key: string; label: string; category: string | null; color: string
  icon: string | null; order_index: number; is_active: boolean; is_system: boolean
}

const CATEGORII = [
  { v: '', t: 'fara categorie' },
  { v: 'cazare', t: 'cazare' },
  { v: 'masa', t: 'masa' },
  { v: 'stil', t: 'stil de calatorie' },
  { v: 'ocazii', t: 'ocazii' },
  { v: 'evitat', t: 'de evitat' },
]
const gol = { label: '', category: '', color: '#6366f1', icon: '' }

export function SetariObservatii() {
  const qc = useQueryClient()
  const [editez, setEditez] = useState<number | 'nou' | null>(null)
  const [f, setF] = useState<typeof gol>(gol)
  const [eroare, setEroare] = useState<string | null>(null)

  const lista = useQuery({
    queryKey: ['observatii'],
    queryFn: () => api.get<{ observations: Obs[] }>('/observations/', { active_only: false }),
  })

  const reia = () => qc.invalidateQueries({ queryKey: ['observatii'] })
  const prinde = (e: unknown) => setEroare(e instanceof Error ? e.message : 'Nu s-a putut salva')

  const salveaza = useMutation({
    mutationFn: () => {
      const corp = { label: f.label, category: f.category || null, color: f.color, icon: f.icon || null }
      return editez === 'nou' ? api.post('/observations/', corp) : api.put(`/observations/${editez}`, corp)
    },
    onSuccess: () => { setEditez(null); setF(gol); reia() }, onError: prinde,
  })
  const comuta = useMutation({
    mutationFn: (o: Obs) => api.put(`/observations/${o.id}`, { is_active: !o.is_active }),
    onSuccess: reia, onError: prinde,
  })
  const sterge = useMutation({
    mutationFn: (id: number) => api.del(`/observations/${id}`), onSuccess: reia, onError: prinde,
  })
  const muta = useMutation({
    mutationFn: (ordine: number[]) => api.post('/observations/reorder', ordine),
    onSuccess: reia, onError: prinde,
  })

  if (lista.isPending) return <div className="px-8 py-6"><Card><Skeleton rows={7} /></Card></div>
  const obs = lista.data?.observations ?? []

  const deschide = (o?: Obs) => {
    setEroare(null)
    if (o) {
      setEditez(o.id)
      setF({ label: o.label, category: o.category ?? '', color: o.color, icon: o.icon ?? '' })
    } else { setEditez('nou'); setF(gol) }
  }

  const mutaCu = (idx: number, directie: -1 | 1) => {
    const noua = [...obs]
    const tinta = idx + directie
    if (tinta < 0 || tinta >= noua.length) return
    const a = noua[idx]
    const b = noua[tinta]
    if (!a || !b) return
    noua[idx] = b
    noua[tinta] = a
    muta.mutate(noua.map((o) => o.id))
  }

  return (
    <div className="px-8 py-6 space-y-5">
      <Card className="p-4 flex flex-wrap items-center justify-between gap-3">
        <p className="text-[13.5px] text-muted">
          Etichetele care se bifeaza pe fisa clientului: prefera etaj inalt, alergic la fructe de
          mare, aniversare in calatorie. Se aleg dintr-o lista, ca sa nu se scrie de fiecare data.
        </p>
        <Button variant="primary" onClick={() => deschide()}>Eticheta noua</Button>
      </Card>

      {eroare && <Card className="p-3"><span className="text-[13px] text-bad">{eroare}</span></Card>}

      {editez !== null && (
        <Grup titlu={editez === 'nou' ? 'Eticheta noua' : 'Modifica eticheta'}>
          <div className="grid gap-4 sm:grid-cols-2 max-w-2xl">
            <Camp eticheta="Text" id="o-text" valoare={f.label} obligatoriu
              seteaza={(v) => setF((x) => ({ ...x, label: v }))} />
            <Camp eticheta="Categorie" id="o-cat" tip="select" optiuni={CATEGORII} valoare={f.category}
              seteaza={(v) => setF((x) => ({ ...x, category: v }))} />
            <Camp eticheta="Emoji" id="o-icon" valoare={f.icon} indiciu="Optional, un singur semn"
              seteaza={(v) => setF((x) => ({ ...x, icon: v }))} />
            <div>
              <label htmlFor="o-culoare" className="text-[12.5px] text-muted mb-1 block">Culoare</label>
              <input id="o-culoare" type="color" value={f.color}
                onChange={(e) => setF((x) => ({ ...x, color: e.target.value }))}
                className="h-9 w-20 rounded-[var(--radius-control)] border border-line bg-surface" />
            </div>
          </div>
          <div className="mt-4 flex gap-3">
            <Button variant="primary" onClick={() => { setEroare(null); salveaza.mutate() }}
              disabled={salveaza.isPending || !f.label}>
              {salveaza.isPending ? 'Se salveaza…' : 'Salveaza'}
            </Button>
            <Button variant="quiet" onClick={() => { setEditez(null); setEroare(null) }}>Renunta</Button>
          </div>
        </Grup>
      )}

      <Card className="overflow-hidden">
        {obs.length === 0 ? (
          <EmptyState title="Nicio eticheta" hint="Adauga prima eticheta de observatii." />
        ) : (
          <ul>
            {obs.map((o, i) => (
              <li key={o.id} className="flex flex-wrap items-center gap-3 px-4 py-2.5 border-b border-line last:border-0">
                <span className="h-4 w-4 rounded-full shrink-0" style={{ background: o.color }} />
                <span className="text-[14px]">{o.icon ? `${o.icon} ` : ''}{o.label}</span>
                {o.category && <span className="text-[12.5px] text-muted">{o.category}</span>}
                <Badge tone={o.is_active ? 'good' : 'neutral'}>{o.is_active ? 'activa' : 'oprita'}</Badge>
                {o.is_system && <span className="text-[12px] text-muted">predefinita</span>}
                <span className="ml-auto flex items-center gap-1.5">
                  <Button variant="quiet" onClick={() => mutaCu(i, -1)} disabled={i === 0 || muta.isPending}>↑</Button>
                  <Button variant="quiet" onClick={() => mutaCu(i, 1)} disabled={i === obs.length - 1 || muta.isPending}>↓</Button>
                  <Button variant="quiet" onClick={() => deschide(o)}>Modifica</Button>
                  <Button variant="quiet" onClick={() => comuta.mutate(o)} disabled={comuta.isPending}>
                    {o.is_active ? 'Opreste' : 'Porneste'}
                  </Button>
                  {!o.is_system && (
                    <Button variant="quiet" onClick={() => {
                      if (confirm(`Stergi eticheta „${o.label}"?`)) sterge.mutate(o.id)
                    }}>Sterge</Button>
                  )}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  )
}
